import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiClock, FiCheckCircle, FiAward, FiTool } from 'react-icons/fi';

const DallasEuropeanAutoRepair = () => {
  const brands = [
    { name: 'BMW', url: '/dallas-bmw-repair', specialty: 'Ultimate Driving Machine Experts' },
    { name: 'Mercedes-Benz', url: '/dallas-mercedes-repair', specialty: 'Three-Pointed Star Specialists' },
    { name: 'Porsche', url: '/dallas-porsche-repair', specialty: 'Performance & Precision' },
    { name: 'Audi', url: '/dallas-audi-repair', specialty: 'Vorsprung durch Technik' },
    { name: 'Volkswagen', url: '/services/european-car-repair/volkswagen', specialty: 'The People\'s Car Pros' },
    { name: 'Mini Cooper', url: '/services/european-car-repair/mini-cooper', specialty: 'Small Car, Big Service' }
  ];

  const dallasAreas = [
    'Dallas', 'Richardson', 'Plano', 'Frisco', 'McKinney', 'Allen',
    'Garland', 'Mesquite', 'Carrollton', 'Lewisville', 'Flower Mound', 'Addison'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      <Helmet>
        <title>European Auto Repair Dallas | BMW, Mercedes, Porsche Specialists | Paradise-Performance</title>
        <meta name="description" content="Expert European auto repair in Dallas, TX. professional specialists for BMW, Mercedes, Porsche, Audi. Same-day service, genuine parts, 1-year warranty." />
        <link rel="canonical" href="https://paradise-performance.com/dallas-european-auto-repair" />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - European Auto Repair Dallas",
            "description": "Expert European auto repair in Dallas specializing in BMW, Mercedes, Porsche, Audi",
            "url": "https://paradise-performance.com/dallas-european-auto-repair",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "116 W Garza Ave",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": [
              {
                "@type": "City",
                "name": "Dallas",
                "@id": "https://www.wikidata.org/wiki/Q16557"
              },
              {
                "@type": "State",
                "name": "Texas"
              }
            ],
            "serviceArea": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": 32.7767,
                "longitude": -96.7970
              },
              "geoRadius": "50 miles"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "European Auto Repair Services",
              "itemListElement": [
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "BMW Repair Dallas"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Mercedes Repair Dallas"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Porsche Repair Dallas"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Audi Repair Dallas"}}
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "487"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section with Dallas Focus */}
      <section className="relative bg-gradient-to-br from-gray-900 to-primary-800 text-white py-24">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.h1 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Expert European Auto Repair in Dallas, TX
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl md:text-2xl mb-8 text-gray-100"
            >
              BMW • Mercedes • Porsche • Audi Specialists Serving the Dallas-Fort Worth Metroplex
            </motion.p>
            
            {/* Trust Signals */}
            <motion.div 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-wrap gap-6 mb-8 text-lg"
            >
              <div className="flex items-center">
                <FiAward className="mr-2 text-yellow-400" />
                <span>Serving Dallas Since 1998</span>
              </div>
              <div className="flex items-center">
                <FiCheckCircle className="mr-2 text-green-400" />
                <span>ASE expert technicians</span>
              </div>
              <div className="flex items-center">
                <FiTool className="mr-2 text-blue-400" />
                <span>Factory-Level Diagnostics</span>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="tel:9403490038"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold text-lg transition-colors"
              >
                <FiPhone className="mr-2" />
                Call (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Service
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dallas Service Area Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Proudly Serving Dallas and Surrounding Areas
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {dallasAreas.map((area, index) => (
              <motion.div
                key={area}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-md p-4 text-center hover:shadow-lg transition-shadow"
              >
                <span className="font-medium">{area}</span>
              </motion.div>
            ))}
          </div>
          <p className="text-center mt-8 text-gray-600">
            Conveniently located only 60 minutes from downtown Dallas via US-380
          </p>
        </div>
      </section>

      {/* Brand-Specific Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            Dallas European Car Repair Specialists
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            From routine maintenance to complex diagnostics, our Dallas-area customers trust us with their 
            European vehicles. Click on your car brand below for specialized service information.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {brands.map((brand, index) => (
              <motion.div
                key={brand.name}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={brand.url}
                  className="block bg-white rounded-lg shadow-lg hover:shadow-xl transition-all p-8 text-center group"
                >
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-primary-600 transition-colors">
                    {brand.name} Repair Dallas
                  </h3>
                  <p className="text-gray-600 mb-4">{brand.specialty}</p>
                  <span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold group-hover:bg-primary-600 group-hover:text-white transition-colors">
                    Learn More →
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Dallas Drivers Choose Us */}
      <section className="py-16 bg-primary-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Why Dallas Drivers Choose Paradise-Performance
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold mb-4">Dallas to Paradise - Worth the Drive</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>30% less expensive than Dallas dealerships</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>Same-day service for most repairs</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>Free shuttle service from Dallas area</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>Loaner vehicles available for Dallas customers</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold mb-4">Expertise DFW Trusts</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>Factory-trained European specialists</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>Latest diagnostic equipment for all European brands</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>OEM and genuine parts in stock</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span>1 year/12,000 mile warranty on all repairs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Common Services for Dallas European Cars */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common European Auto Repairs for Dallas Drivers
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-3">BMW Services</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Oil Service & Inspection</li>
                <li>• Cooling System Repairs</li>
                <li>• VANOS System Service</li>
                <li>• Transmission Service</li>
                <li>• Electronic Diagnostics</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-3">Mercedes Services</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Service A & B Maintenance</li>
                <li>• Airmatic Suspension</li>
                <li>• Transmission Fluid Service</li>
                <li>• Brake System Service</li>
                <li>• Engine Diagnostics</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-lg mb-3">Porsche Services</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• IMS Bearing Replacement</li>
                <li>• PDK Transmission Service</li>
                <li>• Performance Tuning</li>
                <li>• Brake Upgrades</li>
                <li>• Cayenne Maintenance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Dallas-Specific Content for SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2>European Auto Repair Near Dallas - Paradise-Performance</h2>
            <p>
              When Dallas residents need expert European auto repair, they trust Paradise-Performance. 
              located only 60 minutes north of Dallas via US-380, we've been the go-to European car 
              specialists for the DFW Metroplex since shortly after we opened. Our facility in Paradise, TX, offers 
              Dallas drivers a better alternative to expensive dealership service.
            </p>
            
            <h3>Serving All of Dallas County and Beyond</h3>
            <p>
              Whether you're coming from North Dallas, Richardson, Plano, or Downtown Dallas, 
              Paradise-Performance is your nearest independent European auto repair expert. We 
              regularly service vehicles for customers from Highland Park, University Park, 
              Preston Hollow, and all Dallas neighborhoods.
            </p>
            
            <h3>Why Dallas BMW and Mercedes Owners Choose Us</h3>
            <p>
              Dallas is home to more European luxury vehicles per capita than almost any other 
              Texas city. These sophisticated machines require specialized knowledge and equipment 
              that most Dallas auto shops simply don't have. At Paradise-Performance, we invest 
              in the same diagnostic tools and training that Dallas BMW, Mercedes, and Porsche 
              dealerships use, but offer our services at significantly lower prices.
            </p>
            
            <h3>Easy Access from Dallas</h3>
            <p>
              Getting to Paradise-Performance from Dallas is simple: Take US-380 West from Dallas 
              for approximately 30 minutes. We're located in Paradise, TX 76073. 
              Many Dallas customers drop off their vehicle and use our shuttle service or loaner 
              cars to return to Dallas while we service their European vehicle.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Dallas's Trusted European Auto Repair Specialists
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Join thousands of satisfied Dallas-area customers who trust us with their European vehicles
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="tel:9403490038"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
            >
              <FiPhone className="mr-2" />
              Call Now: (817) 382-2316
            </a>
            <div className="text-white">
              <p className="text-sm">Open Monday-Friday 8AM-5PM</p>
              <p className="text-sm">Saturday 9AM-3PM</p>
            </div>
          </div>
          
          {/* Location Info */}
          <div className="mt-12 bg-white/10 rounded-lg p-6 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold mb-3">Visit Us from Dallas</h3>
            <p className="flex items-center justify-center mb-2">
              <FiMapPin className="mr-2" />
              116 W Garza Ave, Paradise, TX 76073
            </p>
            <p className="text-sm">
              Just 40 minutes from North Dallas • 35 minutes from Richardson • 25 minutes from Denton
            </p>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default DallasEuropeanAutoRepair;
